import React, { createContext, useCallback, useContext, useState } from "react";

import {
  EdAppGroupMigrationType,
  RoleDetails,
  StartEdAppCoursewareMigrationRequest,
  StartEdAppCustomerMigrationRequest,
  StartReverseTrainingMigrationRequest,
  StartTrainingDataMigrationRequest,
  StartTrainingSyncOrgRequest,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { SCEmployee } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";
import { StartHealCourseEnrolmentsScriptRequest } from "@safetyculture/s12-apis-connect-web/s12/training/scripts/v1/courseware_pb";
import { StartCompareScAndTrainingGroupsScriptRequest } from "@safetyculture/s12-apis-connect-web/s12/training/scripts/v1/groups_pb";
import { StartCompareScAndTrainingUsersScriptRequest } from "@safetyculture/s12-apis-connect-web/s12/training/scripts/v1/users_pb";
import { GetComparisonSpreadsheetRequest } from "@safetyculture/s12-apis-connect-web/s12/training/translationsupgrades/v1/requests_pb";

import { supportServiceConnectClient } from "../../../../../api";
import { callOptionsHeaders } from "../../../../../api/helpers";
import { GrpcError } from "../../../../../context/snackbar-context";
import { getScUserEmail } from "../../../../../helpers/get-sc-user-email";
import { getScUserName } from "../../../../../helpers/get-sc-user-name";
import type {
  CompareGroupsFormState,
  CompareUsersFormState,
  CoursewareMigrationFormState,
  DataMigrationFormState,
  EdappMigrationFormState,
  HealCourseEnrolmentsFormState,
  SyncOrgFormState,
  TrainingMigrationContextProps,
} from "./types";
import { toUUID } from "helpers/s12-helpers";

const TrainingMigrationContext = createContext<
  TrainingMigrationContextProps | undefined
>(undefined);

export const useTrainingMigrationContext = () => {
  const context = useContext(TrainingMigrationContext);
  if (!context) {
    throw new Error(
      "useTrainingMigrationContext must be used within a TrainingMigrationProvider",
    );
  }
  return context;
};

interface FormProviderProps {
  children: React.ReactNode;
  roleId: string;
  regionCode: string;
  showError: (e: GrpcError) => void;
  orgDetails: RoleDetails;
}

export const TrainingMigrationProvider: React.FC<FormProviderProps> = ({
  children,
  roleId,
  regionCode,
  showError,
  orgDetails,
}) => {
  const initialDataMigrationState: DataMigrationFormState = {
    sourceRegion: orgDetails.isMigratedToScInfra
      ? regionCode.toLocaleUpperCase()
      : "TrainingLegacy",
    targetRegion: regionCode.toLocaleUpperCase(),
    enableGracePeriod: true,
    activityCount: 10,
    workerCount: 5,
    mapActivityCount: 10,
    enableBulkProcessing: true,
  };

  const [dataMigrationFormState, setDataMigrationFormState] =
    useState<DataMigrationFormState>(initialDataMigrationState);

  const initialEdappMigrationState: EdappMigrationFormState = {
    forcePassword: false,
    password: "",
    createGroupsAs: "sites",
    region: regionCode.toUpperCase(),
    applicationId: "",
    skipUserGroupsMigration: false,
    organizationOwnerEmail: "",
    edAppRolesOverrideScSeatPermissions: false,
  };

  const [edappMigrationFormState, setEdappMigrationFormState] =
    useState<EdappMigrationFormState>(initialEdappMigrationState);

  const initialCompareUsersState: CompareUsersFormState = {
    dryRun: true,
    createMissingUsers: false,
  };

  const [compareUsersFormState, setCompareUsersFormState] =
    useState<CompareUsersFormState>(initialCompareUsersState);

  const initialCompareGroupsState: CompareGroupsFormState = {
    dryRun: true,
    createMissingGroups: false,
  };

  const [compareGroupsFormState, setCompareGroupsFormState] =
    useState<CompareGroupsFormState>(initialCompareGroupsState);

  const initialHealCourseEnrolmentsState: HealCourseEnrolmentsFormState = {
    dryRun: true,
    processAllCourses: true,
    courseIds: "",
    useTrainingWatchService: true,
  };

  const [healCourseEnrolmentsFormState, setHealCourseEnrolmentsFormState] =
    useState<HealCourseEnrolmentsFormState>(initialHealCourseEnrolmentsState);

  const initialCoursewareMigrationState: CoursewareMigrationFormState = {
    sourceApplicationId: "",
    adminEmail: "",
    adminPassword: "",
    orgName: "",
  };

  const [coursewareMigrationFormState, setCoursewareMigrationFormState] =
    useState<CoursewareMigrationFormState>(initialCoursewareMigrationState);

  const initialSyncOrgState: SyncOrgFormState = {
    region: regionCode.toLocaleUpperCase(),
    reference: "",
    syncOrgDataOnly: true,
    syncOrgDirectory: false,
  };

  const [syncOrgFormState, setSyncOrgFormState] =
    useState<SyncOrgFormState>(initialSyncOrgState);

  const [dataMigrationDialogOpen, setDataMigrationDialogOpen] = useState(false);
  const [edappMigrationDialogOpen, setEdappMigrationDialogOpen] =
    useState(false);
  const [
    reverseTrainingMigrationDialogOpen,
    setReverseTrainingMigrationDialogOpen,
  ] = useState(false);
  const [compareUsersDialogOpen, setCompareUsersDialogOpen] = useState(false);
  const [compareUsersSuccessDialogOpen, setCompareUsersSuccessDialogOpen] =
    useState(false);
  const [compareUsersBackgroundJobId, setCompareUsersBackgroundJobId] =
    useState("");
  const [compareGroupsDialogOpen, setCompareGroupsDialogOpen] = useState(false);
  const [compareGroupsSuccessDialogOpen, setCompareGroupsSuccessDialogOpen] =
    useState(false);
  const [compareGroupsBackgroundJobId, setCompareGroupsBackgroundJobId] =
    useState("");
  const [healCourseEnrolmentsDialogOpen, setHealCourseEnrolmentsDialogOpen] =
    useState(false);
  const [
    healCourseEnrolmentsSuccessDialogOpen,
    setHealCourseEnrolmentsSuccessDialogOpen,
  ] = useState(false);
  const [
    healCourseEnrolmentsBackgroundJobId,
    setHealCourseEnrolmentsBackgroundJobId,
  ] = useState("");
  const [coursewareMigrationDialogOpen, setCoursewareMigrationDialogOpen] =
    useState(false);
  const [syncOrgDialogOpen, setSyncOrgDialogOpen] = useState(false);

  const [isDownloadingSpreadsheet, setIsDownloadingSpreadsheet] =
    useState(false);

  const [isStartingTranslationUpgrade, setIsStartingTranslationUpgrade] =
    useState(false);

  const resetDataMigrationFormState = () => {
    setDataMigrationFormState({ ...initialDataMigrationState });
  };

  const resetEdappMigrationFormState = () => {
    setEdappMigrationFormState({ ...initialEdappMigrationState });
  };

  const resetCompareUsersFormState = () => {
    setCompareUsersFormState({ ...initialCompareUsersState });
  };

  const resetCompareGroupsFormState = () => {
    setCompareGroupsFormState({ ...initialCompareGroupsState });
  };

  const resetHealCourseEnrolmentsFormState = () => {
    setHealCourseEnrolmentsFormState({ ...initialHealCourseEnrolmentsState });
  };

  const resetCoursewareMigrationFormState = () => {
    setCoursewareMigrationFormState({ ...initialCoursewareMigrationState });
  };

  const resetSyncOrgFormState = () => {
    setSyncOrgFormState({ ...initialSyncOrgState });
  };

  const handleDownloadTranslationUpgradeSpreadsheet = async () => {
    setIsDownloadingSpreadsheet(true);
    try {
      // Call the API without a request object since GetComparisonSpreadsheetRequest doesn't exist
      const response =
        await supportServiceConnectClient.downloadTranslationsUpgradeSpreadsheet(
          new GetComparisonSpreadsheetRequest({
            organisationId: toUUID(roleId),
          }),
          {
            headers: callOptionsHeaders(regionCode, roleId),
          },
        );

      // Convert byte array to Uint8Array for proper binary handling
      const byteArray = new Uint8Array(response.spreadsheetContent);

      // Create blob from byte array for Excel file
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "translation-upgrade-spreadsheet.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsDownloadingSpreadsheet(false);
    }
  };

  const handleStartTranslationsUpgrade = async () => {
    setIsStartingTranslationUpgrade(true);
    try {
      await supportServiceConnectClient.triggerTranslationsUpgrade(
        new GetComparisonSpreadsheetRequest({
          organisationId: toUUID(roleId),
        }),
        {
          headers: callOptionsHeaders(regionCode, roleId),
        },
      );
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsStartingTranslationUpgrade(false);
    }
  };

  const handleStartDataMigration = async () => {
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();

    const req = new StartTrainingDataMigrationRequest({
      orgId: roleId,
      sourceRegion: dataMigrationFormState.sourceRegion,
      targetRegion: dataMigrationFormState.targetRegion,
      scEmployee: new SCEmployee({
        name: scUserName,
        email: scUserEmail,
      }),
      activityCount: dataMigrationFormState.activityCount,
      workerCount: dataMigrationFormState.workerCount,
      enableGracePeriod: dataMigrationFormState.enableGracePeriod,
      mapActivityCount: dataMigrationFormState.mapActivityCount,
      enableBulkProcessing: dataMigrationFormState.enableBulkProcessing,
      orgName: orgDetails.role?.name || "",
    });

    supportServiceConnectClient
      .startTrainingDataMigration(req, {
        headers: callOptionsHeaders(regionCode, roleId),
      })
      .catch((error) => {
        showError(error);
      });
  };

  const handleStartEdappMigration = useCallback(async () => {
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();

    const req = new StartEdAppCustomerMigrationRequest({
      orgId: roleId,
      region: edappMigrationFormState.region,
      applicationId: edappMigrationFormState.applicationId,
      forcedPassword: edappMigrationFormState.forcePassword
        ? edappMigrationFormState.password
        : "",
      groupMigrationType:
        edappMigrationFormState.createGroupsAs === "sites"
          ? EdAppGroupMigrationType.SITES
          : EdAppGroupMigrationType.GROUPS,
      skipGroupMigration: edappMigrationFormState.skipUserGroupsMigration,
      orgOwnerEmail: edappMigrationFormState.organizationOwnerEmail,
      orgName: orgDetails.role?.name || "",
      edAppRolesOverrideScSeatPermissions:
        edappMigrationFormState.edAppRolesOverrideScSeatPermissions,
      scEmployee: new SCEmployee({
        name: scUserName,
        email: scUserEmail,
      }),
    });

    supportServiceConnectClient
      .startEdAppCustomerMigration(req, {
        headers: callOptionsHeaders(regionCode, roleId),
      })
      .catch((error) => {
        showError(error);
      });
  }, [edappMigrationFormState, roleId, regionCode, showError, orgDetails]);

  const handleStartReverseTrainingMigration = useCallback(async () => {
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();

    const req = new StartReverseTrainingMigrationRequest({
      orgId: roleId,
      region: regionCode.toUpperCase(),
      scEmployee: new SCEmployee({
        name: scUserName,
        email: scUserEmail,
      }),
      orgName: orgDetails.role?.name || "",
    });

    supportServiceConnectClient
      .startReverseTrainingMigration(req, {
        headers: callOptionsHeaders(regionCode, roleId),
      })
      .catch((error) => {
        showError(error);
      });
  }, [roleId, regionCode, showError, orgDetails]);

  const handleStartCompareUsers = useCallback(async () => {
    try {
      const response =
        await supportServiceConnectClient.startCompareScAndTrainingUsersScript(
          new StartCompareScAndTrainingUsersScriptRequest({
            organizationId: toUUID(roleId),
            dryRun: compareUsersFormState.dryRun,
            createMissingUsers: compareUsersFormState.createMissingUsers,
          }),
          {
            headers: callOptionsHeaders(regionCode, roleId),
          },
        );

      setCompareUsersBackgroundJobId(response.backgroundJobId);
      setCompareUsersDialogOpen(false);
      setCompareUsersSuccessDialogOpen(true);
    } catch (error) {
      showError(error as GrpcError);
    }
  }, [roleId, regionCode, showError, compareUsersFormState]);

  const handleStartCompareGroups = useCallback(async () => {
    try {
      const response =
        await supportServiceConnectClient.startCompareScAndTrainingGroupsScript(
          new StartCompareScAndTrainingGroupsScriptRequest({
            organizationId: toUUID(roleId),
            dryRun: compareGroupsFormState.dryRun,
            createMissingGroups: compareGroupsFormState.createMissingGroups,
          }),
          {
            headers: callOptionsHeaders(regionCode, roleId),
          },
        );

      setCompareGroupsBackgroundJobId(response.backgroundJobId);
      setCompareGroupsDialogOpen(false);
      setCompareGroupsSuccessDialogOpen(true);
    } catch (error) {
      showError(error as GrpcError);
    }
  }, [roleId, regionCode, showError, compareGroupsFormState]);

  const handleStartHealCourseEnrolments = useCallback(async () => {
    try {
      const response =
        await supportServiceConnectClient.startHealCourseEnrolmentsScript(
          new StartHealCourseEnrolmentsScriptRequest({
            organizationId: toUUID(roleId),
            dryRun: healCourseEnrolmentsFormState.dryRun,
            processAllCourses: healCourseEnrolmentsFormState.processAllCourses,
            courseIds: healCourseEnrolmentsFormState.courseIds,
            useTrainingWatchService:
              healCourseEnrolmentsFormState.useTrainingWatchService,
          }),
          {
            headers: callOptionsHeaders(regionCode, roleId),
          },
        );

      setHealCourseEnrolmentsBackgroundJobId(response.backgroundJobId);
      setHealCourseEnrolmentsDialogOpen(false);
      setHealCourseEnrolmentsSuccessDialogOpen(true);
    } catch (error) {
      showError(error as GrpcError);
    }
  }, [roleId, regionCode, showError, healCourseEnrolmentsFormState]);

  const handleStartCoursewareMigration = useCallback(() => {
    const req = new StartEdAppCoursewareMigrationRequest({
      orgId: roleId,
      region: regionCode.toUpperCase(),
      sourceApplicationId: coursewareMigrationFormState.sourceApplicationId,
      adminEmail: coursewareMigrationFormState.adminEmail,
      adminPassword: coursewareMigrationFormState.adminEmail
        ? coursewareMigrationFormState.adminPassword
        : "",
      orgName: coursewareMigrationFormState.orgName,
    });

    supportServiceConnectClient
      .startEdAppCoursewareMigration(req, {
        headers: callOptionsHeaders(regionCode, roleId),
      })
      .catch((error) => {
        showError(error);
      })
      .finally(() => {
        resetCoursewareMigrationFormState();
      });
  }, [coursewareMigrationFormState, roleId, regionCode, showError]);

  const handleStartSyncOrg = useCallback(async () => {
    const scUserName = await getScUserName();
    const scUserEmail = await getScUserEmail();

    const req = new StartTrainingSyncOrgRequest({
      orgId: roleId,
      region: syncOrgFormState.region,
      syncRequestReference: syncOrgFormState.reference,
      syncOrgDataOnly: syncOrgFormState.syncOrgDataOnly,
      syncOrgDirectory: syncOrgFormState.syncOrgDirectory,
      scEmployee: new SCEmployee({ name: scUserName, email: scUserEmail }),
    });

    supportServiceConnectClient
      .startTrainingSyncOrg(req, {
        headers: callOptionsHeaders(regionCode, roleId),
      })
      .catch((error) => {
        showError(error);
      });
  }, [syncOrgFormState, roleId, regionCode, showError]);

  return (
    <TrainingMigrationContext.Provider
      value={{
        // Data Migration
        dataMigrationFormState,
        setDataMigrationFormState,
        resetDataMigrationFormState,
        dataMigrationDialogOpen,
        setDataMigrationDialogOpen,
        handleStartDataMigration,

        // Edapp Migration
        edappMigrationFormState,
        setEdappMigrationFormState,
        resetEdappMigrationFormState,
        edappMigrationDialogOpen,
        setEdappMigrationDialogOpen,
        handleStartEdappMigration,

        // Reverse Training Migration
        reverseTrainingMigrationDialogOpen,
        setReverseTrainingMigrationDialogOpen,
        handleStartReverseTrainingMigration,

        // Translations
        handleDownloadTranslationUpgradeSpreadsheet,
        handleStartTranslationsUpgrade,
        isDownloadingSpreadsheet,
        isStartingTranslationUpgrade,

        // Compare Users
        compareUsersFormState,
        setCompareUsersFormState,
        resetCompareUsersFormState,
        compareUsersDialogOpen,
        setCompareUsersDialogOpen,
        compareUsersSuccessDialogOpen,
        setCompareUsersSuccessDialogOpen,
        compareUsersBackgroundJobId,
        setCompareUsersBackgroundJobId,
        handleStartCompareUsers,

        // Compare Groups
        compareGroupsFormState,
        setCompareGroupsFormState,
        resetCompareGroupsFormState,
        compareGroupsDialogOpen,
        setCompareGroupsDialogOpen,
        compareGroupsSuccessDialogOpen,
        setCompareGroupsSuccessDialogOpen,
        compareGroupsBackgroundJobId,
        setCompareGroupsBackgroundJobId,
        handleStartCompareGroups,

        // Heal Course Enrolments
        healCourseEnrolmentsFormState,
        setHealCourseEnrolmentsFormState,
        resetHealCourseEnrolmentsFormState,
        healCourseEnrolmentsDialogOpen,
        setHealCourseEnrolmentsDialogOpen,
        healCourseEnrolmentsSuccessDialogOpen,
        setHealCourseEnrolmentsSuccessDialogOpen,
        healCourseEnrolmentsBackgroundJobId,
        setHealCourseEnrolmentsBackgroundJobId,
        handleStartHealCourseEnrolments,

        // Courseware Migration
        coursewareMigrationFormState,
        setCoursewareMigrationFormState,
        resetCoursewareMigrationFormState,
        coursewareMigrationDialogOpen,
        setCoursewareMigrationDialogOpen,
        handleStartCoursewareMigration,

        // Sync Org
        syncOrgFormState,
        setSyncOrgFormState,
        resetSyncOrgFormState,
        syncOrgDialogOpen,
        setSyncOrgDialogOpen,
        handleStartSyncOrg,
      }}
    >
      {children}
    </TrainingMigrationContext.Provider>
  );
};
